import type { ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { numSx } from '../../../../components/common/page/numberStyles';

export const PANEL_ROW_HEIGHT = 24;
export const PANEL_PAD_X = 1.5;

export const panelFrameSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '4px',
    boxShadow: 3,
    overflow: 'hidden',
} as const;

export const panelTableHeadSx = {
    fontSize: '11px',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
} as const;

export const panelCellSx = {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...numSx,
} as const;

export const panelRowSx = {
    height: PANEL_ROW_HEIGHT,
    alignItems: 'center',
    px: 0.75,
    borderRadius: '3px',
} as const;

interface PanelHeadProps {
    title: ReactNode;
    meta?: ReactNode;
    leading?: ReactNode;
    children?: ReactNode;
}

export function PanelHead({ title, meta, leading, children }: PanelHeadProps) {
    return (
        <Box
            sx={{
                px: PANEL_PAD_X,
                py: 1.25,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {leading}
                <Typography
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '13px',
                        fontWeight: 700,
                        lineHeight: 1.3,
                    }}
                >
                    {title}
                </Typography>
                {children}
            </Box>
            {meta && (
                <Typography
                    sx={{
                        fontSize: '12px',
                        color: 'text.secondary',
                        mt: 0.25,
                        ...numSx,
                    }}
                >
                    {meta}
                </Typography>
            )}
        </Box>
    );
}

interface PanelBodyProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
}

export function PanelBody({ children, sx = [] }: PanelBodyProps) {
    return (
        <Box
            sx={[
                { px: PANEL_PAD_X, py: 1 },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {children}
        </Box>
    );
}

export function PanelFoot({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                px: PANEL_PAD_X,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderTop: 1,
                borderColor: 'divider',
                fontSize: '11px',
                color: 'text.secondary',
            }}
        >
            {children}
        </Box>
    );
}

export function PanelNote({
    children,
    color,
}: {
    children: ReactNode;
    color?: string;
}) {
    return (
        <Typography sx={{ fontSize: '11px', color: color ?? 'text.secondary' }}>
            {children}
        </Typography>
    );
}
